/**
 * Grounding Tool - Source Citation (Vodafone Showcase)
 * 
 * This tool is used to report which sources from the knowledge base were used
 * to formulate a response. This enables transparency and citation tracking.
 * 
 * NOTE: This tool is for the Vodafone showcase and is NOT registered by default.
 * It's kept in the repository for future use.
 */

import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';

/**
 * Knowledge base reference for validating source IDs
 * In a production scenario, this would query the actual knowledge base
 */
const VALID_SOURCES: Record<string, { title: string; chunk: string }> = {
  'vodafone_5g': {
    title: 'Vodafone 5G Network Coverage',
    chunk:
      'Vodafone 5G is available in major cities across Germany, providing ultra-fast mobile internet speeds up to 1 Gbps. The network covers metropolitan areas and is continuously expanding to rural regions.'
  },
  'vodafone_tariffs': {
    title: 'Vodafone Mobile Tariffs',
    chunk:
      'Vodafone offers various mobile tariffs including Red, Young, and GigaMobil plans. All plans include unlimited calls and SMS within Germany.'
  },
  'vodafone_iot': {
    title: 'Vodafone IoT Solutions',
    chunk:
      'Vodafone provides comprehensive IoT solutions for businesses, including global connectivity, device management, and analytics platforms.'
  },
  'vodafone_customer_service': {
    title: 'Vodafone Customer Service',
    chunk:
      'Vodafone customer service is available 24/7 through multiple channels: phone hotline, online chat, mobile app, and retail stores.'
  },
  'vodafone_business': {
    title: 'Vodafone Business Solutions',
    chunk:
      'Vodafone Business offers tailored solutions for companies of all sizes, including mobile contracts with volume discounts, unified communications, and cloud services.'
  },
  'vodafone_network_quality': {
    title: 'Vodafone Network Quality',
    chunk:
      'Vodafone network has been rated as one of the best in Germany by independent tests. Our 4G LTE network covers 99% of the population.'
  },
  'vodafone_roaming': {
    title: 'Vodafone Roaming Services',
    chunk:
      'Vodafone customers can use their mobile plan in the entire EU at no extra cost. Outside the EU, we offer travel data passes for affordable roaming.'
  },
  'vodafone_digital_services': {
    title: 'Vodafone Digital Services',
    chunk:
      'Vodafone offers various digital services including streaming partnerships (GigaTV, music services), cloud storage, mobile security, and device insurance.'
  }
};

// Pattern to validate source IDs (alphanumeric and underscores only)
const KEY_PATTERN = /^[a-zA-Z0-9_=\-]+$/;

/**
 * Tool schema for the grounding tool
 */
export const groundingToolVodafoneSchema: ToolSchema = {
  type: 'function',
  name: 'report_grounding',
  description:
    'Report use of a source from the knowledge base as part of an answer (effectively, cite the source). ' +
    'Sources appear in square brackets before each knowledge base passage. Always use this tool to cite ' +
    'sources when responding with information from the knowledge base.',
  parameters: {
    type: 'object',
    properties: {
      sources: {
        type: 'array',
        items: {
          type: 'string',
          description: 'Source ID from the knowledge base'
        },
        description:
          'List of source names from last statement actually used, do not include the ones not used to formulate a response'
      }
    },
    required: ['sources'],
    additionalProperties: false
  }
};

/**
 * Tool implementation for the grounding tool
 */
export const groundingToolVodafoneImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  try {
    const { sources } = args;

    if (!Array.isArray(sources)) {
      return {
        success: false,
        error: 'Invalid sources parameter. Expected an array of source IDs.'
      };
    }

    // Validate and filter source IDs
    const validatedSources = sources.filter(
      (source) => typeof source === 'string' && KEY_PATTERN.test(source)
    );

    if (validatedSources.length === 0) {
      console.warn('⚠️ No valid sources provided for grounding');
      return {
        success: true,
        data: { sources: [] }
      };
    }

    console.log(`📚 Grounding Vodafone sources: ${validatedSources.join(', ')}`);

    // Build citation data
    const citationData = validatedSources
      .filter((sourceId) => VALID_SOURCES[sourceId])
      .map((sourceId) => ({
        chunk_id: sourceId,
        title: VALID_SOURCES[sourceId].title,
        chunk: VALID_SOURCES[sourceId].chunk
      }));

    if (citationData.length === 0) {
      console.warn('⚠️ No valid sources found in knowledge base');
      return {
        success: true,
        data: { sources: [] }
      };
    }

    console.log(`✅ Cited ${citationData.length} sources`);

    // Return citation data
    // Note: In the VoiceRAG implementation, this is sent to the client
    // For client-side implementation, we log it and return success
    return {
      success: true,
      data: {
        sources: citationData
      }
    };
  } catch (error) {
    console.error('❌ Grounding tool error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred during grounding'
    };
  }
};

